import * as yup from "yup";
import { Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "../../../components/ui/button";
import Common from "../../../helper/common";
import {
  FormikInput,
  FormikTextArea,
  FormikSelect,
  FormikSingleFileUpload,
} from "../../../components/formik-components";
import { respEnum, ImageFolderEnum } from "../../../constants/appConstant";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { Checkbox } from "../../../components/ui/checkbox";

const FormikInitialValues = {
  name: "",
  desc: "",
  thumbnail: null,
  regular_price: "",
  special_price: "",
  categoryid: "",
  sortOrder: "",
  isdisplayontop: false,
  isdisplayonapp: false,
  isactive: true,
};

const formikValidation = yup.object({
  name: yup
    .string()
    .trim()
    .required("Product Name is required")
    .max(100, "Product name cannot exceed 100 characters"),
  desc: yup
    .string()
    .trim()
    .max(500, "Description cannot exceed 500 characters"),
  regular_price: yup
    .number()
    .positive("Regular price must be positive")
    .required("Regular price is required")
    .max(999999, "Price cannot exceed 999,999"),
  special_price: yup
    .number()
    .positive("Special price must be positive")
    .nullable()
    .max(999999, "Price cannot exceed 999,999")
    .when("regular_price", {
      is: (regular_price) => regular_price && regular_price > 0,
      then: (schema) =>
        schema.max(
          yup.ref("regular_price"),
          "Special price must be less than regular price"
        ),
      otherwise: (schema) => schema,
    }),
  categoryid: yup.number().required("Category is required"),
  sortOrder: yup
    .number()
    .integer("Sort order must be a whole number")
    .min(0, "Sort order cannot be negative")
    .nullable()
    .max(9999, "Sort order cannot exceed 9,999"),
});

const AddProduct = ({ id, onClose, open, setOpen }) => {
  const formikRef = useRef();
  const dialogRef = useRef();
  const navigate = useNavigate();
  const [isFormikInitialized, setIsFormikInitialized] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    setIsFormikInitialized(open);
    if (open) {
      getCategories();
    }
  }, [open]);

  const getCategories = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetProductCategories"
      );
      if (response?.status === respEnum.Success) {
        setCategories(response.data);
      }
    } catch (error) {
      // If categories API doesn't exist, provide empty array
      setCategories([{ value: "", label: "Select Category (Optional)" }]);
    }
  };

  const onSubmitData = async (d, action) => {
    // Handle image upload
    const imagePath = d.files?.[0].path;
    if (imagePath) {
      d.thumbnail = imagePath;
    }

    if (d.files && d.files.filter((item) => !item.imageUrl).length) {
      var uploadFilePathType = ImageFolderEnum.products;
      d.thumbnail = (
        await Common.Utility.handleFileUpload(d.files, uploadFilePathType)
      )[0].photo;
      d.files = null;
    } else if (d.attachment) {
      d.thumbnail = d.attachment;
    }

    let data = {
      ...d,
      id: id || 0,
      categoryid: d.categoryid || null,
      special_price: d.special_price || null,
      sortOrder: d.sortOrder || null,
      thumbnail: d.thumbnail || null,
    };

    let response = await Common.ApiService.getInstance().request(
      "AddUpdateProduct",
      data,
      "POST"
    );

    action.setSubmitting(false);

    if (response.status === respEnum.Success) {
      //Common.SweetAlert.alert(response.message, "success");
      Common.AntDesignMessage.success(response.message);
      onClose(response.data);
      setOpen(false);
      if (!onClose) {
        navigate("/app/products", { replace: true });
      }
    } else {
      Common.SweetAlert.alert(response.message || "An error occurred", "error");
    }
  };

  const getDataInfo = async () => {
    let response = await Common.ApiService.getInstance().request(
      `GetProductById?id=${id}`
    );
    if (response.status === respEnum.Success && response.data) {
      const productData = {
        ...response.data,
        categoryid: response.data.categoryid || "",
        special_price: response.data.special_price || "",
        sortOrder: response.data.sortOrder || "",
      };
      formikRef.current.setValues(productData);
    }
  };

  useEffect(() => {
    if (id && isFormikInitialized) {
      getDataInfo();
    }
  }, [id, isFormikInitialized]);

  return (
    <div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            size="sm"
            variant="primary"
            className="flex items-center gap-2"
          >
            <Icon icon="heroicons:plus-20-solid" className="h-4 w-4" />
            Add Product
          </Button>
        </DialogTrigger>
        <DialogContent
          ref={dialogRef}
          className="max-w-[95vw] w-[95vw] max-h-[95vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="text-base font-medium flex items-center gap-2">
              <Icon
                icon={!id ? "heroicons:plus-circle" : "heroicons:pencil-square"}
                className="h-5 w-5 text-blue-600"
              />
              {!id ? "Add New" : "Update"} Product
            </DialogTitle>
          </DialogHeader>

          <div className="text-sm text-default-500 space-y-6">
            <Formik
              innerRef={formikRef}
              onSubmit={onSubmitData}
              initialValues={FormikInitialValues}
              validationSchema={formikValidation}
              validateOnChange={false}
              validateOnBlur={false}
              enableReinitialize={true}
            >
              {(formikProps) => (
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                      <Icon
                        icon="heroicons:information-circle"
                        className="h-4 w-4"
                      />
                      Basic Information
                    </h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div className="lg:col-span-2">
                        <FormikInput
                          formikProps={formikProps}
                          name={"name"}
                          label={"Product Name *"}
                          placeholder={"Enter product name"}
                          maxLength="100"
                        />
                      </div>
                      <div className="lg:col-span-2">
                        <FormikTextArea
                          formikProps={formikProps}
                          name={"desc"}
                          label={"Description"}
                          placeholder={"Enter product description"}
                          maxLength="500"
                          rows={3}
                        />
                      </div>
                      <div>
                        <FormikSelect
                          formikProps={formikProps}
                          name={"categoryid"}
                          label={"Category *"}
                          data={categories}
                          placeholder="Select category"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Pricing & Stock */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                      <Icon
                        icon="heroicons:currency-dollar"
                        className="h-4 w-4"
                      />
                      Pricing & Image
                    </h3>
                    <div className="space-y-4">
                      {/* Pricing Row */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <FormikInput
                            formikProps={formikProps}
                            name={"regular_price"}
                            label={"Regular Price *"}
                            placeholder={"99"}
                            type="number"
                            min="0"
                            // step="0.01"
                            onChange={(value) => {
                              formikProps.setFieldValue(
                                "regular_price",
                                Math.round(value)
                              );
                              // Clear special price if it becomes greater than regular price
                              if (
                                formikProps.values.special_price &&
                                value &&
                                formikProps.values.special_price >= value
                              ) {
                                formikProps.setFieldValue("special_price", "");
                              }
                            }}
                          />
                        </div>
                        <div>
                         <FormikInput
                            formikProps={formikProps}
                            name={"special_price"}
                            label={"Special Price"}
                            placeholder={"99"}
                            type="number"
                            max={formikProps.values.regular_price || undefined}
                            onChange={(value) => {
                              if (value === "" || value === null || value === undefined) {
                                // If no value provided, clear the field
                                formikProps.setFieldValue("special_price", "");
                                return;
                              }

                              // Ensure positive number
                              const positiveValue = Math.abs(Math.round(value));

                              // Don't allow special price >= regular price
                              if (
                                formikProps.values.regular_price &&
                                positiveValue >= formikProps.values.regular_price
                              ) {
                                // Don't allow special price to be greater than or equal to regular price
                                return;
                              }

                              formikProps.setFieldValue("special_price", positiveValue);
                            }}
                          />
                          {formikProps.values.regular_price && (
                            <p className="text-xs text-gray-500 mt-1">
                              Must be less than Rs.{" "}
                              {Number(
                                formikProps.values.regular_price
                              ).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Image Upload Row */}
                      <div className="grid grid-cols-1 lg:grid-cols-1 gap-4">
                        <div className="lg:col-span-1">
                          <FormikSingleFileUpload
                            formikProps={formikProps}
                            title={"Product Image"}
                            uploadMainTxt={"Upload Image"}
                            uploadTxt={"Max size 2 MB (JPG, PNG, WEBP)"}
                            name={"thumbnail"}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Display Settings */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                      <Icon icon="heroicons:cog-6-tooth" className="h-4 w-4" />
                      Display Settings
                    </h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div>
                        <FormikInput
                          formikProps={formikProps}
                          name={"sortOrder"}
                          label={"Sort Order"}
                          placeholder={"0"}
                          type="number"
                          min="0"
                        />
                      </div>
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="isdisplayontop"
                            checked={formikProps.values.isdisplayontop}
                            onCheckedChange={(value) =>
                              formikProps.setFieldValue("isdisplayontop", value)
                            }
                          />
                          <label
                            htmlFor="isdisplayontop"
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            Display on Top
                          </label>
                        </div>
                        {false && (
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="isdisplayonapp"
                              checked={formikProps.values.isdisplayonapp}
                              onCheckedChange={(value) =>
                                formikProps.setFieldValue(
                                  "isdisplayonapp",
                                  value
                                )
                              }
                            />
                            <label
                              htmlFor="isdisplayonapp"
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              Display on App
                            </label>
                          </div>
                        )}
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="isactive"
                            checked={formikProps.values.isactive}
                            onCheckedChange={(value) =>
                              formikProps.setFieldValue("isactive", value)
                            }
                          />
                          <label
                            htmlFor="isactive"
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            Active
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Preview Section */}
                  {(formikProps.values.regular_price ||
                    formikProps.values.special_price) && (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <h3 className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-2">
                        <Icon icon="heroicons:eye" className="h-4 w-4" />
                        Preview
                      </h3>
                      <div className="flex items-center gap-3">
                        {formikProps.values.special_price ? (
                          <>
                            <span className="text-lg font-bold text-green-600">
                              Rs.{" "}
                              {Number(
                                formikProps.values.special_price
                              ).toLocaleString()}
                            </span>
                            <span className="text-sm text-gray-500 line-through">
                              Rs.{" "}
                              {Number(
                                formikProps.values.regular_price || 0
                              ).toLocaleString()}
                            </span>
                            <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">
                              {formikProps.values.regular_price &&
                              formikProps.values.special_price
                                ? `${Math.round(
                                    ((formikProps.values.regular_price -
                                      formikProps.values.special_price) /
                                      formikProps.values.regular_price) *
                                      100
                                  )}% OFF`
                                : "SALE"}
                            </span>
                          </>
                        ) : (
                          <span className="text-lg font-bold text-gray-700">
                            Rs.{" "}
                            {Number(
                              formikProps.values.regular_price || 0
                            ).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Formik>
          </div>

          <DialogFooter className="mt-8 gap-2">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                <Icon icon="heroicons:x-mark" className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </DialogClose>

            {isFormikInitialized && formikRef.current && (
              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  formikRef.current?.handleSubmit();
                }}
                disabled={formikRef.current?.isSubmitting}
                className="min-w-[120px]"
              >
                {formikRef.current.isSubmitting ? (
                  <Icon
                    icon="heroicons:arrow-path"
                    className="h-4 w-4 mr-2 animate-spin"
                  />
                ) : (
                  <Icon
                    icon={!id ? "heroicons:plus" : "heroicons:check"}
                    className="h-4 w-4 mr-2"
                  />
                )}
                {formikRef.current.isSubmitting
                  ? "Saving..."
                  : !id
                  ? "Add Product"
                  : "Update Product"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddProduct;
